import { useRef, useState } from "react";
import { Camera, Save, X, LoaderCircle } from "lucide-react";
import ProfileIconButton from "./ProfileIconButton";

export default function ProfilePhotoEditor({ photoSrc, fullName, initials, canEdit, onSave }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [failedPhoto, setFailedPhoto] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const src = preview || photoSrc;

  async function choosePhoto(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setMessage(null);
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || !file.size || file.size > 3 * 1024 * 1024) {
      setMessage({ error: true, text: "Choose a JPG, PNG, or WebP photo up to 3 MB." });
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Unable to read this photo."));
        reader.readAsDataURL(file);
      });
      await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = () => reject(new Error("Choose a valid image file."));
        image.src = dataUrl;
      });
      setFailedPhoto("");
      setPreview(dataUrl);
    } catch (error) {
      setMessage({ error: true, text: error.message });
    } finally {
      setBusy(false);
    }
  }

  async function savePhoto() {
    if (!preview || busy || !canEdit) return;
    setBusy(true);
    setMessage(null);
    try {
      await onSave({ empPicBase64: preview.split(",", 2)[1] });
      setPreview("");
      setMessage({ text: "Photo updated." });
    } catch (error) {
      setMessage({ error: true, text: error.message || "Unable to update photo. Please try again." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-start gap-2 shrink-0 max-w-[220px]">
      <div className="relative">
        <div className="w-16 h-16 rounded-full bg-primary/10 text-primary grid place-items-center text-xl font-bold">
          {src && failedPhoto !== src ? (
            <img src={src} alt={fullName + " - employee photo"} className="w-full h-full rounded-full object-cover"
              onError={() => setFailedPhoto(src)} />
          ) : initials}
        </div>
        {canEdit && <>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            aria-label="Choose employee photo" onChange={choosePhoto} disabled={busy} />
          <ProfileIconButton icon={busy ? LoaderCircle : Camera} spinning={busy} label="Update photo"
            className="absolute -bottom-1 -right-2 bg-white" disabled={busy} onClick={() => inputRef.current?.click()} />
        </>}
      </div>
      {preview && canEdit && <div className="flex gap-2 mt-1">
        <ProfileIconButton icon={busy ? LoaderCircle : Save} spinning={busy} label="Save photo" disabled={busy} onClick={savePhoto} />
        <ProfileIconButton icon={X} label="Cancel photo update" disabled={busy} onClick={() => { setPreview(""); setMessage(null); }} />
      </div>}
      {message && <p role={message.error ? "alert" : "status"} className={"text-xs m-0 " + (message.error ? "text-error" : "text-success")}>
        {message.text}
      </p>}
    </div>
  );
}
